<template>
  <v-dialog
    :class="'v-dialog__wrapper'"
    :content-class="contentClass"
    :value="value"
    max-width="600"
    :overlay-color="overlayColor"
    :overlay-opacity="overlayOpacity"
    @click:outside="clickOutside"
    @keydown.esc="closeModal()"
    :persistent="persistent"
    v-bind="$attrs">
    <div class="v-dialog__content-wrapper">
      <div v-if="activeDialogIcon" class="v-dialog__header">
        <img :src="activeDialogIcon" alt="Close" />
        <v-icon @click="closeModal" v-if="!hideCloseIcon" class="v-dialog__close-btn" :size="24">
          {{ mdiClose }}
        </v-icon>
      </div>
      <div class="v-dialog__title-wrapper d-flex align-center">
        <slot name="title">
          <div class="v-dialog__title" v-html="title"></div>
        </slot>
        <v-icon v-if="!activeDialogIcon && !hideCloseIcon" @click="closeModal" class="v-dialog__close-btn" :size="24">
          {{ mdiClose }}
        </v-icon>
      </div>
      <slot name="subtitle">
        <div class="v-dialog__subtitle" v-html="subtitle"></div>
      </slot>
      <slot name="default"></slot>
    </div>
    <div
      v-if="!hideActions"
      :class="['v-dialog__actions-wrapper', actionsBorder ? 'v-dialog__actions-wrapper--bordered' : '']">
      <slot name="actions">
        <div class="v-dialog__actions">
          <v-btn v-if="!cancelHidden" @click="closeModal" class="v-dialog__actions--cancel" outlined>Cancel</v-btn>
          <v-btn
            :data-cy="ctaCyText"
            :loading="ctaLoading"
            :disabled="ctaDisabled"
            @click="confirm"
            class="v-dialog__actions--confirm"
            :color="ctaColor"
            >{{ ctaText }}</v-btn
          >
        </div>
      </slot>
    </div>
  </v-dialog>
</template>

<script>
  import { mdiClose } from "@mdi/js";
  import generalIcon from "@/assets/icons/modals/general_icon.svg";
  import generalIconDark from "@/assets/icons/modals/general_icon_dark.svg";
  import infoIcon from "@/assets/icons/modals/info_icon.svg";
  import infoIconDark from "@/assets/icons/modals/info_icon_dark.svg";
  import errorIcon from "@/assets/icons/modals/error_icon.svg";
  import errorIconDark from "@/assets/icons/modals/error_icon_dark.svg";
  import successIcon from "@/assets/icons/modals/success_icon.svg";
  import successIconDark from "@/assets/icons/modals/success_icon_dark.svg";
  import dangerIcon from "@/assets/icons/modals/danger_icon.svg";
  import dangerIconDark from "@/assets/icons/modals/danger_icon_dark.svg";
  import creditCardIcon from "@/assets/icons/modals/credit_card.svg";

  export default {
    name: "Modal",
    props: {
      value: {
        type: Boolean,
        required: true,
      },
      title: {
        type: String,
        required: false,
      },
      subtitle: {
        type: String,
        default: "",
      },
      actionsBorder: {
        type: Boolean,
        default: false,
      },
      dialogIcon: {
        type: String,
        default: "",
      },
      ctaText: {
        type: String,
        required: false,
        default: "Select",
      },
      ctaColor: {
        type: String,
        default: "primary",
      },
      ctaDisabled: {
        type: Boolean,
        default: false,
      },
      ctaLoading: {
        type: Boolean,
        default: false,
      },
      ctaCyText: {
        type: String,
        default: "",
      },
      hideActions: {
        type: Boolean,
        default: false,
      },
      contentClass: {
        type: String,
        default: "",
      },
      hideCloseIcon: {
        type: Boolean,
        default: false,
      },
      cancelHidden: {
        type: Boolean,
        default: false,
      },
      persistent: {
        type: Boolean,
        default: false,
      },
      overlayColor: {
        type: String,
        default: "rgb(33, 33, 33)",
      },
      overlayOpacity: {
        type: String,
        default: "0.46",
      },
    },
    emits: ["cancel", "confirm"],
    setup(props, { emit }) {
      const closeModal = () => {
        emit("input", false);
        emit("cancel");
      };

      const confirm = () => {
        emit("confirm");
      };

      return {
        closeModal,
        confirm,
        mdiClose,
        generalIcon,
      };
    },
    computed: {
      clickOutside() {
        return this.persistent ? () => {} : this.closeModal;
      },
      activeDialogIcon() {
        if (!this.dialogIcon) return null;

        const icons = {
          general: {
            light: generalIcon,
            dark: generalIconDark,
          },
          info: {
            light: infoIcon,
            dark: infoIconDark,
          },
          error: {
            light: errorIcon,
            dark: errorIconDark,
          },
          success: {
            light: successIcon,
            dark: successIconDark,
          },
          danger: {
            light: dangerIcon,
            dark: dangerIconDark,
          },
          creditCard: {
            light: creditCardIcon,
            dark: creditCardIcon,
          },
        };

        return icons[this.dialogIcon]?.[this.$vuetify.theme.dark ? "dark" : "light"];
      },
    },
  };
</script>

<style lang="scss" scoped>
  .v-dialog__wrapper {
    // If dragNDrop input is behind the dialog, clicking anywhere on the dialog triggers file exporer to be opened,
    // That's why we need to set higher z-index on the modal
    z-index: 9999 !important;
  }
</style>
